import type { Slice } from "@/store/createSlice";
import { VARIANCE_TO_BETA } from "@/store/types";
import type { TournamentFormat } from "@/types";
import { type Activity, ActivityFormat, type ActivityVariance } from "@/types";
import type { ActivityId, PlayerId } from "@/types/branded";
import { toActivityId } from "@/types/branded";
import { generateUid } from "@/utils/index";

type record = Record<never, never>;

type BaseActivityParams<TRecord = record> = {
  name: string;
  variance: ActivityVariance;
  format: ActivityFormat;
  weight: number;
  participantIds: PlayerId[];
} & TRecord;

export type FreeForAllActivityParams = BaseActivityParams<{
  format: ActivityFormat.FreeForAll;
  topK: number;
}>;

export type TeamsActivityParams = BaseActivityParams<{
  format: ActivityFormat.Teams;
  numTeams: number;
  teamSizes: number[];
  randomizeTeams: boolean;
  topK: number;
}>;

export type TournamentActivityParams = BaseActivityParams<{
  format: ActivityFormat.Tournament;
  tournamentFormat: TournamentFormat;
  knockoutEliminations?: number;
}>;

export type ActivityParams =
  | FreeForAllActivityParams
  | TeamsActivityParams
  | TournamentActivityParams;

export const isFreeForAllParams = (params: ActivityParams): params is FreeForAllActivityParams =>
  params.format === ActivityFormat.FreeForAll;

export const isTeamsParams = (params: ActivityParams): params is TeamsActivityParams =>
  params.format === ActivityFormat.Teams;

export const isTournamentParams = (params: ActivityParams): params is TournamentActivityParams =>
  params.format === ActivityFormat.Tournament;

export const activityToParams = (activity: Activity): ActivityParams => {
  switch (activity.format) {
    case ActivityFormat.FreeForAll:
      return activity;
    case ActivityFormat.Teams:
      return activity;
    case ActivityFormat.Tournament:
      return activity;
    default: {
      const _exhaustiveCheck: never = activity;
      throw new Error(`Unsupported activity format: ${(activity as Activity).format}`);
    }
  }
};

const createDefaultFreeForAllActivity = (
  id: ActivityId,
  params: FreeForAllActivityParams,
): Activity => ({
  id,
  name: params.name,
  beta: VARIANCE_TO_BETA[params.variance],
  weight: params.weight,
  variance: params.variance,
  format: params.format,
  participantIds: params.participantIds,
  topK: params.topK,
});

const createDefaultTeamsActivity = (id: ActivityId, params: TeamsActivityParams): Activity => ({
  id,
  name: params.name,
  beta: VARIANCE_TO_BETA[params.variance],
  weight: params.weight,
  variance: params.variance,
  format: params.format,
  participantIds: params.participantIds,
  numTeams: params.numTeams,
  teamSizes: params.teamSizes,
  randomizeTeams: params.randomizeTeams,
  topK: params.topK,
});

const createDefaultTournamentActivity = (
  id: ActivityId,
  params: TournamentActivityParams,
): Activity => ({
  id,
  name: params.name,
  beta: VARIANCE_TO_BETA[params.variance],
  weight: params.weight,
  variance: params.variance,
  format: params.format,
  participantIds: params.participantIds,
  tournamentFormat: params.tournamentFormat,
  knockoutEliminations: params.knockoutEliminations,
});

const createActivity = (id: ActivityId, params: ActivityParams): Activity => {
  switch (params.format) {
    case ActivityFormat.FreeForAll:
      return createDefaultFreeForAllActivity(id, params);
    case ActivityFormat.Teams:
      return createDefaultTeamsActivity(id, params);
    case ActivityFormat.Tournament:
      return createDefaultTournamentActivity(id, params);
    default: {
      const _exhaustiveCheck: never = params;
      throw new Error(`Unsupported activity format: ${(params as ActivityParams).format}`);
    }
  }
};

export const createActivitySlice: Slice<{
  activities: Record<ActivityId, Activity>;
  addActivity: (params: ActivityParams) => void;
  updateActivity: (id: ActivityId, params: ActivityParams) => void;
  deleteActivity: (id: ActivityId) => void;
}> = (set, _get) => ({
  activities: {},

  addActivity: (params) =>
    set(
      (s) => {
        const id = toActivityId(generateUid());
        s.activities[id] = createActivity(id, params);
      },
      false,
      "activity/add",
    ),

  updateActivity: (id, params) =>
    set(
      (s) => {
        if (!s.activities[id]) return;
        s.activities[id] = createActivity(id, params);
      },
      false,
      "activity/update",
    ),

  deleteActivity: (id) =>
    set(
      (s) => {
        delete s.activities[id];
      },
      false,
      "activity/delete",
    ),
});
